/**
 * The whole user manual as one printable HTML document: a cover with the
 * version and the print date, the table of contents and every chapter in
 * the order of the help menu (instruction-catalog). The browser prints it
 * to paper or to a PDF (InstructionPrintService).
 */

import {
  parseInstructionHtml,
  removeLeadingTitle,
  serializeFragment
} from './instruction-html';

export interface ManualChapter {
  slug: string;
  title: string;
  /** A sub-page: indented in the table of contents. */
  sub: boolean;
  /** The rendered page, or null when it could not be loaded. */
  html: string | null;
}

export interface ManualSection {
  id: string;
  label: string;
  chapters: ManualChapter[];
}

export interface ManualTexts {
  /** The document title on the cover, e.g. "Käyttöohje". */
  title: string;
  subtitle: string;
  /** "Versio 2.10" on the cover. */
  version: string;
  printed: string;
  contents: string;
  missing: string;
}

export interface ManualDocumentInput {
  lang: string;
  /** The app's origin: images and fonts are read from it. */
  baseUrl: string;
  logoUrl: string;
  /** The app version, e.g. 2.10, for the page footer. */
  version: string;
  texts: ManualTexts;
  sections: ManualSection[];
}

const HEADING = /^h([1-6])$/i;

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * A chapter's content under its title (h2): the repeated title is dropped,
 * the page's own headings start from h3 whatever level the page uses, and
 * links to other instruction pages go to their chapter in the document
 * (or nowhere, when the page is not in it).
 */
export function prepareChapterHtml(
  html: string,
  title: string,
  slugsInManual: Set<string>
): string {
  const fragment = parseInstructionHtml(html);
  removeLeadingTitle(fragment, title);

  const headings = Array.from(
    fragment.querySelectorAll('h1, h2, h3, h4, h5, h6')
  );
  const levels = headings.map((h) => Number(HEADING.exec(h.tagName)![1]));
  const shift = levels.length ? 3 - Math.min(...levels) : 0;
  headings.forEach((heading, i) => {
    const level = Math.min(6, Math.max(3, levels[i] + shift));
    if (level !== levels[i]) {
      const replacement = heading.ownerDocument.createElement(`h${level}`);
      replacement.innerHTML = heading.innerHTML;
      heading.replaceWith(replacement);
    }
  });

  fragment.querySelectorAll('a[href]').forEach((anchor) => {
    const match = /^\/help\/([^/?#]+)\/?(#.*)?$/.exec(
      anchor.getAttribute('href') ?? ''
    );
    if (!match) {
      return;
    }
    const slug = decodeURIComponent(match[1]);
    if (slugsInManual.has(slug)) {
      anchor.setAttribute('href', `#${slug}`);
    } else {
      anchor.removeAttribute('href');
    }
  });

  // Notes for the manual's authors (<!-- IMAGE MISSING: ... -->).
  const walker = fragment.ownerDocument.createTreeWalker(
    fragment,
    NodeFilter.SHOW_COMMENT
  );
  const comments: Node[] = [];
  while (walker.nextNode()) {
    comments.push(walker.currentNode);
  }
  comments.forEach((comment) => comment.parentNode?.removeChild(comment));

  return serializeFragment(fragment);
}

export function buildManualDocument(input: ManualDocumentInput): string {
  const { texts } = input;
  const sections = input.sections.filter((s) => s.chapters.length);
  const slugs = new Set(sections.flatMap((s) => s.chapters.map((c) => c.slug)));
  const footer = `Aline ${input.version} – ${texts.title}`;

  const toc = sections
    .map(
      (section) =>
        `<li><a href="#section-${section.id}">${escapeHtml(section.label)}</a><ul>` +
        section.chapters
          .map(
            (c) =>
              `<li${c.sub ? ' class="sub"' : ''}><a href="#${c.slug}">${escapeHtml(c.title)}</a></li>`
          )
          .join('') +
        '</ul></li>'
    )
    .join('');

  const body = sections
    .map(
      (section) =>
        `<section class="manual-section"><h1 id="section-${section.id}">${escapeHtml(section.label)}</h1>` +
        section.chapters
          .map(
            (c) =>
              `<article class="chapter"><h2 id="${c.slug}">${escapeHtml(c.title)}</h2>` +
              (c.html === null
                ? `<p class="missing">${escapeHtml(texts.missing)}</p>`
                : prepareChapterHtml(c.html, c.title, slugs)) +
              '</article>'
          )
          .join('') +
        '</section>'
    )
    .join('');

  return `<!doctype html>
<html lang="${escapeHtml(input.lang)}"><head><meta charset="utf-8">
<base href="${escapeHtml(input.baseUrl)}/">
<title>${escapeHtml(footer)}</title>
<style>
@font-face { font-family: 'Noto Sans'; src: url('assets/fonts/NotoSans-Regular.ttf'); font-weight: 400; }
@font-face { font-family: 'Noto Sans'; src: url('assets/fonts/NotoSans-Bold.ttf'); font-weight: 700; }
@page { size: A4; margin: 20mm 18mm;
  @bottom-left { content: "${footer.replace(/"/g, '\\"')}"; font: 9pt 'Noto Sans', sans-serif; color: #666; }
  @bottom-right { content: counter(page); font: 9pt 'Noto Sans', sans-serif; color: #666; } }
@page :first { @bottom-left { content: none; } @bottom-right { content: none; } }
html { font-family: 'Noto Sans', sans-serif; font-size: 10pt; line-height: 1.5; color: #1d1b38; }
body { margin: 0; }
a { color: #4b1fa8; text-decoration: none; }
.cover { height: 250mm; display: flex; flex-direction: column; justify-content: center; break-after: page; }
.cover img { width: 60mm; height: auto; margin-bottom: 14mm; }
.cover h1 { font-size: 34pt; margin: .2em 0 .3em; color: #2a0a6b; }
.cover p { font-size: 12pt; color: #444; margin: .2em 0; }
.toc { break-after: page; }
.toc h1 { font-size: 20pt; color: #2a0a6b; }
.toc > ul { list-style: none; padding: 0; columns: 2; column-gap: 10mm; }
.toc > ul > li { break-inside: avoid; margin-bottom: .8em; font-weight: 700; }
.toc ul ul { list-style: none; padding-left: 0; font-weight: 400; margin-top: .2em; }
.toc li.sub { padding-left: 1.2em; }
.toc a { color: inherit; }
.manual-section > h1 { break-before: page; font-size: 24pt; color: #2a0a6b; border-bottom: 3px solid #6a1fd0; padding-bottom: 4mm; margin-top: 0; }
.chapter { break-before: page; }
.manual-section > h1 + .chapter { break-before: avoid; }
h2 { font-size: 17pt; color: #2a0a6b; margin: 0 0 .6em; }
h3 { font-size: 13pt; color: #2a0a6b; margin: 1.4em 0 .4em; }
h4 { font-size: 11pt; margin: 1.2em 0 .3em; }
h5, h6 { font-size: 10pt; margin: 1em 0 .3em; }
h1, h2, h3, h4, h5, h6 { break-after: avoid; }
p, li { orphans: 3; widows: 3; }
img { max-width: 100%; height: auto; break-inside: avoid; }
p > img:only-child { display: block; margin: .6em 0; border: 1px solid #ddd; }
p img:not(:only-child) { max-height: 1.6em; vertical-align: middle; }
table { width: 100%; border-collapse: collapse; margin: .8em 0; font-size: 8.5pt; line-height: 1.4; }
th, td { border: 1px solid #d6d0e6; padding: 3px 5px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
th { background: #f1edf9; }
thead { display: table-header-group; }
tr { break-inside: avoid; }
code { font-size: 9pt; background: #f4f2f8; padding: 0 3px; }
pre { white-space: pre-wrap; background: #f4f2f8; padding: 6px; }
blockquote { margin: .6em 0; padding: .2em .8em; border-left: 3px solid #6a1fd0; background: #f7f4fc; }
.missing { color: #a00; }
.image-marker { display: inline-block; width: 1.1em; height: .8em; margin-right: .5em; border: 3px solid currentColor; }
.image-marker-green { color: #a2e61b; } .image-marker-pink { color: #ff80ff; }
.image-marker-blue { color: #00aacc; } .image-marker-wine { color: #b31564; }
</style></head><body>
<div class="cover"><img src="${escapeHtml(input.logoUrl)}" alt="Aline">
<h1>${escapeHtml(texts.title)}</h1>
<p>${escapeHtml(texts.subtitle)}</p>
<p>${escapeHtml(texts.version)}</p>
<p>${escapeHtml(texts.printed)}</p></div>
<nav class="toc"><h1>${escapeHtml(texts.contents)}</h1><ul>${toc}</ul></nav>
${body}
</body></html>`;
}
